/**
 * getPlatformConfig — the `platform/config` document, read once per session.
 *
 * Ten surfaces read this doc, two of them (`App` and `MainApp`) in the same
 * mount, and every one of them wrote the same six lines: a `getDoc` in a
 * mount effect, an `exists()` check, one field pulled out, `.catch(() => {})`.
 * It holds the platform accent, the grade scale and a handful of feature
 * switches — settings a platform admin changes a few times a year, fetched
 * fresh on every navigation.
 *
 * Memoized on the PROMISE, not the value, so concurrent callers on a cold boot
 * share one request instead of racing three. A rejection clears the memo so a
 * retry is a real retry — the same reason `loadGoogleMaps` forgets its cached
 * promise on failure.
 *
 * **A session now keeps the config it started with.** Before this, navigating
 * between routes re-read the doc, so a change an admin made mid-session could
 * appear without a reload. It cannot any more, which is the trade this makes:
 * ~10 reads per session down to 1, for a document that changes a few times a
 * year. The admin's own session is covered — `PlatformAdmin` and
 * `PlatformRecapManagement` call `invalidatePlatformConfig()` after they write,
 * so a save is never followed by a stale read in the tab that made it.
 */
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

let inFlight = null;

/**
 * @returns {Promise<object>} The config, or `{}` when the doc does not exist —
 *   never null, so callers can read a field without a guard.
 */
export function getPlatformConfig() {
  if (!inFlight) {
    inFlight = getDoc(doc(db, "platform", "config"))
      .then((snap) => (snap.exists() ? snap.data() : {}))
      .catch((e) => { inFlight = null; throw e; });
  }
  return inFlight;
}

/** Drop the memo. Call after writing the doc. */
export function invalidatePlatformConfig() {
  inFlight = null;
}

/** Test seam — never called by app code. */
export function __resetPlatformConfigForTests() {
  inFlight = null;
}
